/**
 * ChatFilterDrawer
 *
 * Painel lateral direito unico para filtrar a ChatList. Substitui a antiga
 * barra de filtros inline (Tudo/Nao lidas/Agente/Transferido) E a antiga
 * listagem inline de etiquetas — tudo agora vive aqui.
 *
 * Renderizado via createPortal para nao ficar preso ao stacking context da
 * sidebar (que era o motivo do drawer abrir colado a uma margem em vez de
 * preencher a tela inteira a direita).
 *
 * Props:
 *  - isOpen, onClose
 *  - colors                  -> tema (useTheme)
 *  - statusOptions           -> [{ id, label }] das pilulas de status do chat
 *  - statusValue             -> id do status atual ('all' | 'unread' | ...)
 *  - onChangeStatus(id)      -> callback ao mudar status
 *  - labels                  -> [{ id, name, color }] disponiveis (pode vir vazio)
 *  - selectedLabelIds        -> Set<string> com ids selecionados
 *  - onChangeLabels(setIds)  -> callback ao mudar selecao de etiquetas
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import CustomFieldFilterFields from '../contacts/CustomFieldFilterFields';
import {
  X, Search, Check, Filter, GitBranch, ChevronDown, ChevronRight,
  Calendar, MessageSquareDashed, User, Bot, Users, SlidersHorizontal,
} from 'lucide-react';

const ChatFilterDrawer = ({
  isOpen,
  onClose,
  colors,
  statusOptions = [],
  statusValue = 'all',
  onChangeStatus,
  labels = [],
  funnels = [],
  selectedLabelIds = new Set(),
  onChangeLabels,
  // Filtros extras
  members = [],              // [{id, name, type}] responsáveis (membros + agentes)
  memberValue = '',
  onChangeMember,
  sinceValue = '',           // '' | 'today' | '7d' | '30d'
  onChangeSince,
  createdFromValue = '',     // "Entrou no CRM entre": 'YYYY-MM-DD' | ''
  createdToValue = '',
  onChangeCreatedRange,      // (from, to)
  unansweredValue = false,
  onChangeUnanswered,
  onClearExtra,               // limpa responsável+data+sem-resposta numa tacada
  searchInMessages = false,
  onChangeSearchInMessages,
  // Campo personalizado: [{id, name, type, options:[{id,label}]}] do workspace.
  // Vazio (workspace sem campos, ou flag desligada) some com a seção inteira e
  // a numeração fecha o buraco — mesma regra da seção de responsável.
  customFields = [],
  customFieldValues = [],     // [{field_id, value}] selecionados
  onChangeCustomFields,
  // 'funnel' quando aberto pela pílula "Funil" — rola/expande a seção de funil.
  initialFocusSection = null,
}) => {
  const [search, setSearch] = useState('');
  // Funis recolhidos (por padrão TODOS colapsados — resolve o "aparece tudo
  // de uma vez"). Ao buscar etiqueta, os grupos com match abrem sozinhos.
  const [expandedFunnels, setExpandedFunnels] = useState(() => new Set());
  // Dropdown custom de responsável (select nativo não renderiza o ícone Bot).
  const [memberOpen, setMemberOpen] = useState(false);
  const funnelSectionRef = useRef(null);

  useEffect(() => {
    if (!isOpen) { setSearch(''); setMemberOpen(false); }
  }, [isOpen]);

  // Foco na seção de funil (pílula "Funil"): rola até ela e expande os funis.
  // Só na TRANSIÇÃO de abertura — não re-roda quando `funnels` muda (SSE), senão
  // desfaria os colapsos manuais do usuário e re-scrollaria (jank).
  useEffect(() => {
    if (!isOpen || initialFocusSection !== 'funnel') return undefined;
    setExpandedFunnels(new Set((funnels || []).map((f) => f.id)));
    const t = setTimeout(() => {
      funnelSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialFocusSection]);

  // Agrupa as etiquetas (colunas) pelos seus FUNIS, na ordem dos funis. Funil
  // sem etiqueta ainda aparece (indicando "vazio"). Etiquetas órfãs (sem funil
  // válido) caem num grupo "Sem funil".
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (l) => !q || (l.name || '').toLowerCase().includes(q);

    const byFunnel = new Map();
    const orphan = [];
    labels.forEach((l) => {
      if (l.pipeline_id) {
        if (!byFunnel.has(l.pipeline_id)) byFunnel.set(l.pipeline_id, []);
        byFunnel.get(l.pipeline_id).push(l);
      } else {
        orphan.push(l);
      }
    });

    const ordered = [...funnels].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const knownIds = new Set(ordered.map((f) => f.id));
    // pipeline_ids que aparecem nas etiquetas mas não estão na lista de funis
    byFunnel.forEach((ls, fid) => { if (!knownIds.has(fid)) orphan.push(...ls); });

    const funnelGroups = ordered.map((f) => {
      const all = (byFunnel.get(f.id) || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return { funnel: f, labels: all.filter(matches), total: all.length };
    });

    return { funnelGroups, orphan: orphan.filter(matches), orphanTotal: orphan.length };
  }, [labels, funnels, search]);

  const isSearching = search.trim().length > 0;

  if (!isOpen) return null;

  const toggleLabel = (id) => {
    const next = new Set(selectedLabelIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChangeLabels?.(next);
  };

  const clearLabels = () => onChangeLabels?.(new Set());

  const toggleFunnelExpand = (fid) => {
    setExpandedFunnels((prev) => {
      const n = new Set(prev);
      if (n.has(fid)) n.delete(fid); else n.add(fid);
      return n;
    });
  };
  // Selecionar/limpar TODAS as etiquetas de um funil de uma vez.
  const funnelSelState = (fls) => {
    const ids = fls.map((l) => l.id);
    if (!ids.length) return 'none';
    const sel = ids.filter((id) => selectedLabelIds.has(id)).length;
    return sel === 0 ? 'none' : sel === ids.length ? 'all' : 'some';
  };
  const toggleFunnelAll = (fls) => {
    const next = new Set(selectedLabelIds);
    const st = funnelSelState(fls);
    fls.forEach((l) => { if (st === 'all') next.delete(l.id); else next.add(l.id); });
    onChangeLabels?.(next);
  };

  // O status so e considerado "ativo dentro do drawer" quando esta entre as
  // opcoes que o drawer apresenta (filtros inline como "Tudo"/"Nao lidas"
  // ficam fora do escopo do contador interno).
  const drawerStatusActive = statusOptions.some(opt => opt.id === statusValue);
  const extraCount = (memberValue ? 1 : 0) + (sinceValue ? 1 : 0)
    + ((createdFromValue || createdToValue) ? 1 : 0)
    + (unansweredValue ? 1 : 0) + (customFieldValues.length > 0 ? 1 : 0);
  const hasActiveFilters = drawerStatusActive || selectedLabelIds.size > 0 || extraCount > 0;

  const clearAll = () => {
    if (drawerStatusActive) onChangeStatus?.('all');
    if (selectedLabelIds.size > 0) onChangeLabels?.(new Set());
    // Um único reset dos filtros extras — chamar onChangeMember/Since/Unanswered
    // em sequência ressuscitaria os outros via closure stale do notifyExtraFilters.
    if (memberValue || sinceValue || unansweredValue || createdFromValue || createdToValue) onClearExtra?.();
    if (customFieldValues.length > 0) onChangeCustomFields?.([]);
  };

  // Presets de data + rótulo do responsável ativo.
  const dateInputStyle = {
    padding: '7px 10px', borderRadius: 8,
    border: `1px solid ${colors.borderColor}`,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary, fontSize: 12,
  };
  const DATE_PRESETS = [
    { id: 'today', label: 'Hoje' },
    { id: '7d', label: '7 dias' },
    { id: '30d', label: '30 dias' },
  ];

  const renderLabelRow = (label) => {
    const active = selectedLabelIds.has(label.id);
    return (
      <button
        key={label.id}
        onClick={() => toggleLabel(label.id)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '9px 10px',
          background: active ? `${label.color}15` : 'transparent',
          border: 'none', borderRadius: 8,
          cursor: 'pointer', textAlign: 'left',
          transition: 'background-color 0.15s',
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, backgroundColor: label.color, flexShrink: 0 }} />
          <span style={{
            fontSize: 14,
            color: active ? label.color : colors.textPrimary,
            fontWeight: active ? 600 : 400,
          }}>
            {label.name}
          </span>
        </span>
        {active && <Check size={16} color={label.color} />}
      </button>
    );
  };

  const noFunnelsAtAll = funnels.length === 0 && labels.length === 0;

  // Passos numerados em sequência (mesmo idioma dos drawers de Agendar
  // follow-up / Configurar automação): badge nº + pergunta + dica + corpo.
  const stepStyles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 6 },
    head: { display: 'flex', alignItems: 'center', gap: 8 },
    num: {
      width: 22, height: 22, borderRadius: '50%',
      backgroundColor: colors.accentPrimary, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, flexShrink: 0,
    },
    title: { margin: 0, fontSize: 14, fontWeight: 600, color: colors.textPrimary },
    hint: { margin: '0 0 4px 30px', fontSize: 12, color: colors.textSecondary, lineHeight: 1.45 },
    body: { marginLeft: 30, display: 'flex', flexDirection: 'column', gap: 10 },
  };
  // Sem responsáveis (ex.: inbox de Instagram, cujo mapa de assignments não
  // cobre o canal) a seção inteira some e a numeração fecha o buraco.
  const hasMembers = members.length > 0;
  const hasCustomFields = customFields.length > 0;
  const _base = statusOptions.length > 0 ? 1 : 0;
  const numResp = _base + 1;
  const numDate = _base + (hasMembers ? 2 : 1);
  const numUnans = numDate + 1;
  const numMsg = numUnans + 1;
  const numCustom = numMsg + 1;
  const labelStepNum = numMsg + (hasCustomFields ? 2 : 1);

  // Linha de switch reutilizável (Sem-resposta / Buscar em mensagens).
  const SwitchRow = ({ num, icon, title, hint, checked, onToggle }) => (
    <section style={stepStyles.wrap}>
      <div style={{ ...stepStyles.head, justifyContent: 'space-between' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={stepStyles.num}>{num}</span>
          <h4 style={stepStyles.title}>{icon}{title}</h4>
        </span>
        <button
          onClick={() => onToggle?.(!checked)}
          aria-label={title}
          style={{
            width: 42, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            backgroundColor: checked ? colors.accentPrimary : colors.borderColor,
            position: 'relative', transition: 'background-color 0.15s', flexShrink: 0,
          }}
        >
          <span style={{
            position: 'absolute', top: 2, left: checked ? 20 : 2,
            width: 20, height: 20, borderRadius: '50%', backgroundColor: '#fff',
            transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }} />
        </button>
      </div>
      {hint && <p style={stepStyles.hint}>{hint}</p>}
    </section>
  );

  const drawer = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1100,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 380, maxWidth: '100%', height: '100%',
          backgroundColor: colors.bgPrimary,
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px',
          borderBottom: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.headerBg,
        }}>
          <div>
            <h3 style={{
              margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Filter size={16} color={colors.accentPrimary} />
              Filtrar
            </h3>
            <p style={{
              margin: '2px 0 0', fontSize: 11, color: colors.textSecondary,
            }}>
              Refine por status, responsável, data, sem-resposta e funil.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: colors.textSecondary, padding: 6, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — passos numerados em sequência (estilo dos drawers de
            agendar follow-up / configurar automação). */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px', display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* Passo 1 — Status da conversa */}
          {statusOptions.length > 0 && (
            <section style={stepStyles.wrap}>
              <div style={stepStyles.head}>
                <span style={stepStyles.num}>1</span>
                <h4 style={stepStyles.title}>Por status da conversa?</h4>
              </div>
              <p style={stepStyles.hint}>
                Mostre só conversas num estado específico — ou pule e veja todas.
              </p>
              <div style={{ ...stepStyles.body, flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {statusOptions.map((opt) => {
                  const active = statusValue === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => onChangeStatus?.(opt.id)}
                      style={{
                        padding: '8px 14px', borderRadius: 18,
                        border: `1.5px solid ${active ? colors.accentPrimary : colors.borderColor}`,
                        backgroundColor: active ? colors.accentPrimary : 'transparent',
                        color: active ? 'white' : colors.textSecondary,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Responsável — some quando o caller não tem alvos (Instagram). */}
          {hasMembers && (
          <section style={stepStyles.wrap}>
            <div style={stepStyles.head}>
              <span style={stepStyles.num}>{numResp}</span>
              <h4 style={stepStyles.title}>Por responsável?</h4>
            </div>
            <p style={stepStyles.hint}>Só conversas de um consultor ou agente específico.</p>
            <div style={stepStyles.body}>
              {(() => {
                // Círculo com ícone padrão (mesmo do modal de transferência):
                // agente = Bot, membro = User, "todos" = Users.
                const circle = (type) => (
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: type === 'agent' ? `${colors.accentPrimary}22`
                      : type === 'member' ? 'rgba(245,158,11,0.15)' : colors.bgSecondary,
                    color: type === 'agent' ? colors.accentPrimary
                      : type === 'member' ? '#b45309' : colors.textTertiary,
                  }}>
                    {type === 'agent' ? <Bot size={13} />
                      : type === 'member' ? <User size={13} /> : <Users size={13} />}
                  </span>
                );
                const sel = members.find((m) => m.id === memberValue) || null;
                const opts = [{ id: '', name: 'Todos os responsáveis', type: 'all' }, ...members];
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => setMemberOpen((v) => !v)}
                      style={{
                        width: '100%', padding: '7px 12px', borderRadius: 8,
                        border: `1px solid ${memberOpen ? colors.accentPrimary : colors.borderColor}`,
                        backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                        fontSize: 14, cursor: 'pointer', outline: 'none',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      {circle(sel ? sel.type : 'all')}
                      <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sel ? sel.name : 'Todos os responsáveis'}
                      </span>
                      <ChevronDown size={16} style={{
                        flexShrink: 0, color: colors.textTertiary, transition: 'transform 0.15s',
                        transform: memberOpen ? 'rotate(180deg)' : 'none',
                      }} />
                    </button>
                    {memberOpen && (
                      <div style={{
                        marginTop: 6, border: `1px solid ${colors.borderColor}`, borderRadius: 8,
                        overflow: 'hidden', maxHeight: 220, overflowY: 'auto',
                        backgroundColor: colors.bgPrimary,
                      }}>
                        {opts.map((m) => {
                          const active = (m.id || '') === (memberValue || '');
                          return (
                            <button
                              key={m.id || '__all'}
                              type="button"
                              onClick={() => { onChangeMember?.(m.id); setMemberOpen(false); }}
                              onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = colors.bgSecondary; }}
                              onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                              style={{
                                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 10px', border: 'none', cursor: 'pointer', textAlign: 'left',
                                background: active ? colors.bgSecondary : 'transparent',
                                color: colors.textPrimary, fontSize: 14,
                              }}
                            >
                              {circle(m.type)}
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {m.name}
                              </span>
                              {active && <Check size={15} color={colors.accentPrimary} style={{ flexShrink: 0 }} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </section>
          )}

          {/* Data da última mensagem */}
          <section style={stepStyles.wrap}>
            <div style={stepStyles.head}>
              <span style={stepStyles.num}>{numDate}</span>
              <h4 style={stepStyles.title}>Por data da última mensagem?</h4>
            </div>
            <p style={stepStyles.hint}>Só conversas ativas num período recente.</p>
            <div style={{ ...stepStyles.body, flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {DATE_PRESETS.map((p) => {
                const active = sinceValue === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => onChangeSince?.(active ? '' : p.id)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '8px 14px', borderRadius: 18,
                      border: `1.5px solid ${active ? colors.accentPrimary : colors.borderColor}`,
                      backgroundColor: active ? colors.accentPrimary : 'transparent',
                      color: active ? 'white' : colors.textSecondary,
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <Calendar size={12} />{p.label}
                  </button>
                );
              })}
            </div>
            {/* Entrou no CRM entre (data de criação do contato). Core, 4 canais. */}
            <p style={{ ...stepStyles.hint, marginTop: 12 }}>
              Entrou no CRM entre (data em que o contato foi criado; contato migrado conta a data da migração):
            </p>
            <div style={{ ...stepStyles.body, flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <input
                type="date"
                aria-label="Entrou no CRM a partir de"
                value={createdFromValue || ''}
                max={createdToValue || undefined}
                onChange={(e) => onChangeCreatedRange?.(e.target.value || '', createdToValue || '')}
                style={dateInputStyle}
              />
              <span style={{ fontSize: 12, color: colors.textTertiary }}>até</span>
              <input
                type="date"
                aria-label="Entrou no CRM até"
                value={createdToValue || ''}
                min={createdFromValue || undefined}
                onChange={(e) => onChangeCreatedRange?.(createdFromValue || '', e.target.value || '')}
                style={dateInputStyle}
              />
              {(createdFromValue || createdToValue) && (
                <button
                  type="button"
                  onClick={() => onChangeCreatedRange?.('', '')}
                  style={{ background: 'transparent', border: 'none', color: colors.textTertiary, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  limpar
                </button>
              )}
            </div>
          </section>

          {/* Sem resposta */}
          <SwitchRow
            num={numUnans}
            icon={<MessageSquareDashed size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} color={colors.accentPrimary} />}
            title="Só sem resposta"
            hint="Conversas cuja última mensagem foi do contato (o time ainda não respondeu)."
            checked={unansweredValue}
            onToggle={onChangeUnanswered}
          />

          {/* Buscar no conteúdo das mensagens */}
          <SwitchRow
            num={numMsg}
            icon={<Search size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} color={colors.accentPrimary} />}
            title="Buscar no conteúdo das mensagens"
            hint="Ao pesquisar, também procura no TEXTO das mensagens (por padrão só nome e telefone)."
            checked={searchInMessages}
            onToggle={onChangeSearchInMessages}
          />

          {/* Por campo personalizado — some quando o workspace não tem campos */}
          {hasCustomFields && (
          <section style={stepStyles.wrap}>
            <div style={{ ...stepStyles.head, justifyContent: 'space-between' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={stepStyles.num}>{numCustom}</span>
                <h4 style={stepStyles.title}>
                  <SlidersHorizontal size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} color={colors.accentPrimary} />
                  Por campo personalizado?
                </h4>
              </span>
              {customFieldValues.length > 0 && (
                <button
                  onClick={() => onChangeCustomFields?.([])}
                  style={{
                    background: 'transparent', border: 'none',
                    color: colors.textTertiary, fontSize: 11, cursor: 'pointer',
                    textDecoration: 'underline', padding: 0,
                  }}
                >
                  limpar
                </button>
              )}
            </div>
            <p style={stepStyles.hint}>
              Agrupa todos os contatos com o mesmo valor. Escolher dois campos
              restringe (e); dois valores do mesmo campo ampliam (ou).
            </p>
            <div style={stepStyles.body}>
              <CustomFieldFilterFields
                fields={customFields}
                values={customFieldValues}
                onChange={onChangeCustomFields}
                colors={colors}
              />
            </div>
          </section>
          )}

          {/* Passo N — Etapa do funil (etiqueta) */}
          <section ref={funnelSectionRef} style={{ ...stepStyles.wrap, flex: 1, minHeight: 0 }}>
            <div style={{ ...stepStyles.head, justifyContent: 'space-between' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={stepStyles.num}>{labelStepNum}</span>
                <h4 style={stepStyles.title}>Por etapa do funil?</h4>
              </span>
              {selectedLabelIds.size > 0 && (
                <button
                  onClick={clearLabels}
                  style={{
                    background: 'transparent', border: 'none',
                    color: colors.textTertiary, fontSize: 11, cursor: 'pointer',
                    textDecoration: 'underline', padding: 0,
                  }}
                >
                  limpar
                </button>
              )}
            </div>

            {!noFunnelsAtAll && (
              <p style={stepStyles.hint}>
                Toque numa etapa do funil para ver só os contatos nela. Dá pra
                combinar várias.
              </p>
            )}

            <div style={{ ...stepStyles.body, flex: 1, minHeight: 0 }}>

            {labels.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center',
                backgroundColor: colors.bgSecondary, borderRadius: 8,
                padding: '0 10px', border: `1px solid ${colors.borderColor}`,
                marginBottom: 8,
              }}>
                <Search size={14} color={colors.textTertiary} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar etapa..."
                  style={{
                    flex: 1, padding: '8px 10px', border: 'none', background: 'transparent',
                    fontSize: 13, color: colors.textPrimary, outline: 'none',
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {noFunnelsAtAll ? (
                <div style={{ padding: 16, textAlign: 'center', color: colors.textTertiary, fontSize: 13 }}>
                  Você ainda não criou funis ou etapas.<br />
                  Crie na tela <b>Funis</b> para filtrar por etapa do funil.
                </div>
              ) : (
                <>
                  {groups.funnelGroups.map(({ funnel, labels: fls, total }) => {
                    // Ao BUSCAR, escondemos funis sem correspondência (reduz ruído);
                    // sem busca, todo funil aparece — RECOLHIDO por padrão (resolve o
                    // "aparece tudo de uma vez"), abre ao clicar no cabeçalho.
                    if (isSearching && fls.length === 0) return null;
                    const open = isSearching || expandedFunnels.has(funnel.id);
                    const selState = funnelSelState(fls);
                    const selCount = fls.filter((l) => selectedLabelIds.has(l.id)).length;
                    return (
                      <div key={funnel.id} style={{
                        border: `1px solid ${selCount ? colors.accentPrimary : colors.borderColor}`,
                        borderRadius: 10, overflow: 'hidden',
                      }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '8px 10px', backgroundColor: colors.bgSecondary,
                        }}>
                          <button
                            onClick={() => toggleFunnelExpand(funnel.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textTertiary, display: 'flex', padding: 0 }}
                            aria-label={open ? 'Recolher' : 'Expandir'}
                          >
                            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                          <span
                            onClick={() => toggleFunnelExpand(funnel.id)}
                            style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: colors.textPrimary, cursor: 'pointer' }}
                          >
                            <GitBranch size={13} color={colors.accentPrimary} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{funnel.name}</span>
                            <span style={{
                              fontSize: 10, fontWeight: 700,
                              color: selCount ? '#fff' : colors.textTertiary,
                              backgroundColor: selCount ? colors.accentPrimary : colors.bgTertiary,
                              borderRadius: 10, minWidth: 18, height: 18, padding: '0 6px',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              {selCount ? `${selCount}/${total}` : total}
                            </span>
                          </span>
                          {fls.length > 0 && (
                            <button
                              onClick={() => toggleFunnelAll(fls)}
                              style={{ fontSize: 11, fontWeight: 700, color: colors.accentPrimary, background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              {selState === 'all' ? 'Limpar funil' : 'Funil inteiro'}
                            </button>
                          )}
                        </div>
                        {open && (fls.length > 0 ? (
                          <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {fls.map(renderLabelRow)}
                          </div>
                        ) : (
                          <div style={{
                            margin: 8, padding: '8px 12px', fontSize: 12, fontStyle: 'italic',
                            color: colors.textTertiary,
                            border: `1px dashed ${colors.borderColor}`, borderRadius: 8,
                          }}>
                            Funil sem etapas (colunas).
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  {groups.orphan.length > 0 && (
                    <div>
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: colors.textSecondary,
                        padding: '0 4px 6px',
                      }}>
                        Sem funil
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {groups.orphan.map(renderLabelRow)}
                      </div>
                    </div>
                  )}

                  {isSearching
                    && groups.funnelGroups.every((g) => g.labels.length === 0)
                    && groups.orphan.length === 0 && (
                    <div style={{ padding: 16, textAlign: 'center', color: colors.textTertiary, fontSize: 13 }}>
                      Nenhuma etapa encontrada.
                    </div>
                  )}
                </>
              )}
            </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px',
          borderTop: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.bgSecondary,
        }}>
          <span style={{ fontSize: 12, color: colors.textSecondary }}>
            {(() => {
              const n = (drawerStatusActive ? 1 : 0) + (selectedLabelIds.size > 0 ? 1 : 0) + extraCount;
              return n > 0 ? `${n} filtro${n === 1 ? '' : 's'} ativo${n === 1 ? '' : 's'}` : 'Sem filtros ativos';
            })()}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {hasActiveFilters && (
              <button
                onClick={clearAll}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 13,
                  backgroundColor: 'transparent', color: colors.textSecondary,
                  border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
                }}
              >
                Limpar tudo
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 18px', backgroundColor: colors.accentPrimary,
                color: 'white', border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Concluído
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
};

export default ChatFilterDrawer;
